import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Instagram, MapPin, ChevronDown, ChevronRight } from 'lucide-react';
import BookingModal from './BookingModal';
import { serviceCategories } from '../data/servicesData';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isServicesExpanded, setIsServicesExpanded] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const location = useLocation();
  
  const isHomepage = location.pathname === '/';
  const isAboutPage = location.pathname === '/about' || location.pathname.startsWith('/location/');
  const isDarkPage = isAboutPage || isHomepage;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location]);

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (isSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isSidebarOpen]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  // Determine navbar style based on page and scroll
  const getNavbarBg = () => {
    if (isScrolled) {
      return 'bg-black/90 backdrop-blur-md';
    }
    return 'bg-transparent';
  };

  return (
    <>
      {/* Main Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 py-4 ${getNavbarBg()}`}
        data-testid="main-navbar"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-center relative">
          {/* Logo + Brand Name - BNC X Benefits - CENTERED */}
          <Link
            to="/"
            className="flex items-center gap-1 md:gap-2"
            data-testid="nav-logo"
          >
            <img 
              src="/logo.png" 
              alt="BNC Logo" 
              className="h-16 md:h-20 w-auto"
            />
            <span 
              className="text-7xl md:text-8xl leading-none"
              style={{ 
                background: 'linear-gradient(180deg, #D4AF37 0%, #B8860B 50%, #D4AF37 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: '200',
                marginTop: '-6px'
              }}
            >×</span>
            <img 
              src="/benefits-logo.png" 
              alt="Benefits Salon Logo" 
              className="h-20 md:h-24 w-auto"
              style={{ filter: 'invert(1) brightness(2)' }}
            />
          </Link>

          {/* Hamburger Menu Button - Absolute Right - Premium Gold */}
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="absolute right-6 md:right-12 p-2 transition-colors"
            aria-label="Open menu"
            data-testid="hamburger-menu"
            style={{ color: '#D4AF37' }}
          >
            <Menu strokeWidth={1.5} size={32} />
          </button>
        </div>
      </nav>

      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/60 z-[90] transition-opacity duration-300 ${
          isSidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsSidebarOpen(false)}
      />

      {/* Half-Page Sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-full md:w-1/2 lg:w-2/5 bg-black z-[100] transform transition-transform duration-500 ease-out ${
          isSidebarOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="sidebar-menu"
      >
        {/* Sidebar Header */}
        <div className="flex justify-between items-center px-8 py-6 border-b border-white/10">
          <Link 
            to="/" 
            className="flex items-center gap-1"
            onClick={() => setIsSidebarOpen(false)}
          >
            <img 
              src="/logo.png" 
              alt="BNC Logo" 
              className="h-14 w-auto"
            />
            <span 
              className="text-6xl leading-none"
              style={{ 
                background: 'linear-gradient(180deg, #D4AF37 0%, #B8860B 50%, #D4AF37 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: '200',
                marginTop: '-4px'
              }}
            >×</span>
            <img 
              src="/benefits-logo.png" 
              alt="Benefits Salon Logo" 
              className="h-16 w-auto"
              style={{ filter: 'invert(1) brightness(2)' }}
            />
          </Link>
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="p-2 text-white hover:text-[#B8860B] transition-colors"
            aria-label="Close menu"
            data-testid="close-sidebar"
          >
            <X strokeWidth={1.5} size={28} />
          </button>
        </div>

        {/* Navigation Links */}
        <div className="px-8 py-8 overflow-y-auto max-h-[calc(100vh-280px)]">
          <nav className="space-y-1">
            {/* Home Link */}
            <Link
              to="/"
              className={`block py-3 text-2xl md:text-3xl font-light tracking-[0.1em] transition-colors border-b border-white/5 ${
                location.pathname === '/' 
                  ? 'text-[#B8860B]' 
                  : 'text-white hover:text-[#B8860B]'
              }`}
              onClick={() => setIsSidebarOpen(false)}
            >
              Home
            </Link>

            {/* Services with Expandable Categories - 3-Level Navigation (Bodycraft Style) */}
            <div className="border-b border-white/5">
              <button
                onClick={() => {
                  setIsServicesExpanded(!isServicesExpanded);
                  if (isServicesExpanded) setExpandedCategory(null);
                }}
                className={`w-full flex items-center justify-between py-3 text-2xl md:text-3xl font-light tracking-[0.1em] transition-colors ${isServicesExpanded ? 'text-[#B8860B]' : 'text-white hover:text-[#B8860B]'}`}
              >
                <span>Services</span>
                <ChevronDown 
                  size={20} 
                  className={`transition-transform duration-300 ${isServicesExpanded ? 'rotate-180' : ''}`}
                />
              </button>
              
              {/* Level 1: Main Categories */}
              <div className={`overflow-hidden transition-all duration-500 ${isServicesExpanded ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="pl-4 pb-4 space-y-1">
                  {serviceCategories.map((category) => (
                    <div key={category.id} className="border-b border-white/5 last:border-b-0">
                      {/* Category Header - Clickable to expand subcategories */}
                      <button
                        onClick={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)}
                        className={`w-full flex items-center justify-between py-2.5 text-lg font-light transition-colors ${expandedCategory === category.id ? 'text-[#B8860B]' : 'text-white/80 hover:text-[#B8860B]'}`}
                      >
                        <span>{category.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-white/30">{category.subCategories.length}</span>
                          <ChevronRight 
                            size={14} 
                            className={`transition-transform duration-300 text-[#B8860B] ${expandedCategory === category.id ? 'rotate-90' : ''}`}
                          />
                        </div>
                      </button>
                      
                      {/* Level 2: Explore + Sub-Categories with Thumbnails */}
                      <div className={`overflow-hidden transition-all duration-300 ${expandedCategory === category.id ? 'max-h-[1500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="pl-2 pb-4 space-y-3">
                          {/* Explore Category Link - First Item */}
                          <Link
                            to={`/services/${category.slug}`}
                            onClick={() => setIsSidebarOpen(false)}
                            className="flex items-center gap-3 py-2 px-2 bg-[#B8860B]/10 border border-[#B8860B]/30 rounded-lg hover:bg-[#B8860B]/20 transition-colors group"
                          >
                            <img 
                              src={category.image} 
                              alt={category.name}
                              className="w-10 h-10 object-cover rounded"
                            />
                            <div className="flex-1">
                              <span className="text-[#B8860B] text-sm font-medium">Explore {category.name}</span>
                              <p className="text-white/40 text-xs">{category.subCategories.length} categories</p>
                            </div>
                            <ChevronRight size={16} className="text-[#B8860B] group-hover:translate-x-1 transition-transform" />
                          </Link>
                          
                          {/* Sub-Categories with Thumbnails */}
                          <div className="space-y-1 pt-1">
                            <p className="text-white/30 text-xs uppercase tracking-wider px-2 mb-2">Services</p>
                            {category.subCategories.map((subCat) => (
                              <Link
                                key={subCat.id}
                                to={`/services/${category.slug}/${subCat.slug}`}
                                onClick={() => setIsSidebarOpen(false)}
                                className="flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-white/5 transition-colors group"
                              >
                                <img 
                                  src={subCat.image} 
                                  alt={subCat.name}
                                  className="w-10 h-10 object-cover rounded"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-white/80 text-sm font-light truncate group-hover:text-[#B8860B] transition-colors">
                                      {subCat.name}
                                    </span>
                                    {subCat.trending && (
                                      <span className="w-1.5 h-1.5 rounded-full bg-[#B8860B] flex-shrink-0"></span>
                                    )}
                                  </div>
                                  <p className="text-white/30 text-xs">{subCat.services.length} treatments</p>
                                </div>
                                <span className="text-xs text-white/20 group-hover:text-[#B8860B]/50 transition-colors flex-shrink-0">
                                  Explore →
                                </span>
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <Link
                    to="/services"
                    onClick={() => setIsSidebarOpen(false)}
                    className="flex items-center gap-2 py-3 text-sm font-light text-[#B8860B] hover:text-[#9A7B0A] transition-colors border-t border-white/5 mt-2"
                  >
                    Browse All Services →
                  </Link>
                </div>
              </div>
            </div>

            {/* Other Nav Links */}
            <Link
              to="/about"
              className={`block py-3 text-2xl md:text-3xl font-light tracking-[0.1em] transition-colors border-b border-white/5 ${
                location.pathname === '/about' 
                  ? 'text-[#B8860B]' 
                  : 'text-white hover:text-[#B8860B]'
              }`}
              onClick={() => setIsSidebarOpen(false)}
            >
              About
            </Link>
            <Link
              to="/gallery"
              className={`block py-3 text-2xl md:text-3xl font-light tracking-[0.1em] transition-colors border-b border-white/5 ${
                location.pathname === '/gallery' 
                  ? 'text-[#B8860B]' 
                  : 'text-white hover:text-[#B8860B]'
              }`}
              onClick={() => setIsSidebarOpen(false)}
            >
              Gallery
            </Link>
            <Link
              to="/blog"
              className={`block py-3 text-2xl md:text-3xl font-light tracking-[0.1em] transition-colors border-b border-white/5 ${
                location.pathname === '/blog' 
                  ? 'text-[#B8860B]' 
                  : 'text-white hover:text-[#B8860B]'
              }`}
              onClick={() => setIsSidebarOpen(false)}
            >
              Blog
            </Link>
            <Link
              to="/contact"
              className={`block py-3 text-2xl md:text-3xl font-light tracking-[0.1em] transition-colors border-b border-white/5 ${
                location.pathname === '/contact' 
                  ? 'text-[#B8860B]' 
                  : 'text-white hover:text-[#B8860B]'
              }`}
              onClick={() => setIsSidebarOpen(false)}
            >
              Contact
            </Link>
            <Link
              to="/franchise"
              className={`block py-3 text-2xl md:text-3xl font-light tracking-[0.1em] transition-colors border-b border-white/5 ${
                location.pathname === '/franchise' 
                  ? 'text-[#B8860B]' 
                  : 'text-white hover:text-[#B8860B]'
              }`}
              onClick={() => setIsSidebarOpen(false)}
            >
              Franchise
            </Link>
          </nav>

          {/* Book Now Button */}
          <button
            onClick={() => {
              setIsSidebarOpen(false);
              setIsBookingModalOpen(true);
            }}
            className="w-full mt-8 bg-[#B8860B] text-black px-8 py-4 text-sm tracking-[0.2em] uppercase font-medium hover:bg-[#9A7B0A] transition-colors"
          >
            Book Appointment
          </button>
        </div>

        {/* Sidebar Footer - Contact Info */}
        <div className="absolute bottom-0 left-0 right-0 px-8 py-8 border-t border-white/10">
          <div className="space-y-4">
            <a
              href="tel:+917204602211"
              className="flex items-center gap-3 text-white/70 hover:text-[#B8860B] transition-colors"
            >
              <Phone strokeWidth={1.5} size={18} />
              <span className="text-sm tracking-[0.1em]">+91 72046 02211</span>
            </a>
            <a
              href="https://www.instagram.com/blr_nailclub/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-white/70 hover:text-[#B8860B] transition-colors"
            >
              <Instagram strokeWidth={1.5} size={18} />
              <span className="text-sm tracking-[0.1em]">@blr_nailclub</span>
            </a>
            <div className="flex items-center gap-3 text-white/50">
              <MapPin strokeWidth={1.5} size={18} />
              <span className="text-sm tracking-[0.1em]">RR Nagar & Basaveshwarnagar</span>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      <BookingModal isOpen={isBookingModalOpen} onClose={() => setIsBookingModalOpen(false)} />
    </>
  );
};

export default Navbar;
